import isEqual from "lodash/isEqual";

import {
  CONVENTIONS,
  type ConventionId,
  type Position,
  type PositionBasePreset,
  type PositionTransformation,
} from "@/shared/state/visualization/schema/v1/three-d";

import { metreConventionName, type AxisPresetName } from "../axisPresetNames";

import { bindingFor, type TopicDataPathBinding } from "./positionTopicData";

export interface PositionPreset {
  /** The data used to identify and present this preset in a list of presets. */
  name: AxisPresetName;

  /**
   * The original type of preset this preset is based on.
   *
   * Should not be changed when duplicating a preset.
   */
  basePreset: PositionBasePreset;

  /**
   * The coordinate transformation necessary to render data from this preset in the 3D panel.
   *
   * Should not be changed when duplicating a preset.
   */
  transformation: PositionTransformation;

  /**
   * The semantic labels to show for each axis. (Lat/Lon/Alt, East/North/Up, etc).
   *
   * Should not be changed when duplicating a preset.
   */
  labels: {
    forward: string;
    right: string;
    up: string;
  };

  /**
   * Multiplied into each axis value when the 3D panel renders it. A negative
   * scale flips that axis: `north_east_down` carries `up: -1` so a
   * down-positive measurement plots upward in the scene.
   */
  scales: {
    forward: number;
    right: number;
    up: number;
  };

  /**
   * The topic data each axis plots. The presets in {@link presets} carry none,
   * describing a coordinate convention alone.
   */
  bindings?: {
    forward?: TopicDataPathBinding;
    right?: TopicDataPathBinding;
    up?: TopicDataPathBinding;
  };
}

/**
 * The preset describing a position already stored on an entity, so a position
 * the user configured by hand is selectable in the preset list. Undefined when
 * there is no position, or the position names no base preset.
 *
 * 1. The base preset itself, when the position keeps that preset's scales and
 *    binds no axis to topic data.
 * 2. Otherwise a copy of the base preset, titled "Custom" plus the base
 *    preset's title, carrying the position's own scales and per-axis bindings.
 */
export function presetFor(
  position: Position | undefined,
): PositionPreset | undefined {
  if (!position || !position.basePreset) {
    return undefined;
  }
  const basePreset = presets[position.basePreset];
  const scales = {
    forward: position.forwardScale,
    right: position.rightScale,
    up: position.upScale,
  };

  const forwardConfig = bindingFor(position.forward);
  const rightConfig = bindingFor(position.right);
  const upConfig = bindingFor(position.up);

  if (
    isEqual(scales, basePreset.scales) &&
    !forwardConfig &&
    !rightConfig &&
    !upConfig
  ) {
    return basePreset;
  }

  return {
    ...basePreset,
    name: {
      ...basePreset.name,
      title: `Custom (${basePreset.name.title})`,
      formatHint: "",
    },
    scales,
    bindings: {
      forward: forwardConfig,
      right: rightConfig,
      up: upConfig,
    },
  };
}

const baseLatLonAltPreset: PositionPreset = {
  name: {
    title: "Lat/Lon/Alt",
    subtitle: "deg/deg/m",
    formatHint: "Common in PX4/ROS",
  },
  labels: {
    right: "Lat",
    forward: "Lon",
    up: "Alt",
  },
  scales: {
    right: 1,
    forward: 1,
    up: 1,
  },
  transformation: "lla_to_enu",
  basePreset: "lat_lon_alt",
};

/**
 * The slots, labels and scales of a preset whose axes are a Cartesian
 * convention: each slot's label names the source axis the convention puts
 * there, and its scale is the convention's sign for it. Taken from the shared
 * catalogue so a position lands where a point cloud or rotation in the same
 * convention does.
 */
function conventionAxes(
  id: ConventionId,
): Pick<PositionPreset, "labels" | "scales"> {
  const convention = CONVENTIONS[id];
  return {
    labels: {
      forward: convention.axisNames[convention.forward.field],
      right: convention.axisNames[convention.right.field],
      up: convention.axisNames[convention.up.field],
    },
    scales: {
      forward: convention.forward.scale,
      right: convention.right.scale,
      up: convention.up.scale,
    },
  };
}

const baseEastNorthUpPreset: PositionPreset = {
  name: metreConventionName("enu"),
  ...conventionAxes("enu"),
  transformation: null,
  basePreset: "east_north_up",
};

const baseNorthEastDownPreset: PositionPreset = {
  name: metreConventionName("ned"),
  ...conventionAxes("ned"),
  transformation: null,
  basePreset: "north_east_down",
};

/**
 * REP-103's body frame: x forward, y left, z up. Left lands on the scene's
 * −right, so the right slot holds the left axis at a scale of −1.
 */
const baseForwardLeftUpPreset: PositionPreset = {
  name: metreConventionName("flu"),
  ...conventionAxes("flu"),
  transformation: null,
  basePreset: "forward_left_up",
};

/**
 * The scene's own axes, which are left-handed: no right-handed source (ROS,
 * PX4, URDF) belongs here without a flipped axis.
 */
const baseForwardRightUpPreset: PositionPreset = {
  name: {
    title: "Forward/Right/Up",
    subtitle: "m/m/m",
    formatHint: "Left-handed coordinates",
  },
  ...conventionAxes("scene"),
  transformation: null,
  basePreset: "forward_right_up",
};

export const presets: Record<PositionBasePreset, PositionPreset> = {
  lat_lon_alt: baseLatLonAltPreset,
  east_north_up: baseEastNorthUpPreset,
  north_east_down: baseNorthEastDownPreset,
  forward_left_up: baseForwardLeftUpPreset,
  forward_right_up: baseForwardRightUpPreset,
};
